import { useTranslations } from "next-intl";
import { useState, useEffect, useRef } from "react";
import { useHits, useInstantSearch, usePagination } from "react-instantsearch";
import { useRouter } from "next/compat/router";
import ahoy from "@lib/ahoy";
import { analytics } from "@lib/trackers/analytics";
import { useCurrentUser } from "@hooks/use-current-user";
import { useProductsViewedEvent } from "@hooks/use-analytics-events";
import catalogContexts from "@utils/constants/catalogContexts";
import { getProducts } from "@utils/call-api/products-call";
import LowResultsDeleteFilters from "./LowResultsDeleteFilters";
import LowResultsProductSlider from "./LowResultsProductSlider";

const mainIndex = process.env.NEXT_PUBLIC_ALGOLIA_MAIN_INDEX_V2;

function LowResultsSection({
  context,
  getInitializedUiState,
  resetPersonalisedFilters,
  noLowResultSection,
  hub,
  listingPage,
}) {
  const t = useTranslations("catalog");

  const { items } = useHits();
  const { isFirstPage } = usePagination();
  const { status } = useInstantSearch();
  const { uiState, setUiState } = useInstantSearch();
  const router = useRouter();
  const user = useCurrentUser();
  const productsViewedEvent = useProductsViewedEvent();
  const userTagMaternity = user?.user_tags?.find((tag) => tag.key === "maternity")?.value || false;
  const contextRef = useRef("");

  const isSearchOnRentedProducts = uiState?.[mainIndex]?.configure?.filters !== "";
  const isSearchOnLikedProducts = uiState?.[mainIndex]?.refinementList?.id?.length;

  const [isMounted, setIsMounted] = useState(false);
  const [products, setProducts] = useState([]);

  function findQueryString() {
    switch (context) {
      case catalogContexts.swap:
        if (userTagMaternity) return t("low_results_section.maternity.query_string");

        return t("low_results_section.newest.query_string");
      case catalogContexts.sales:
        return t("low_results_section.top_sales.query_string");
      default:
        return t("low_results_section.newest.query_string");
    }
  }

  function sendProductsViewedEvent(productsViewed) {
    const hubOrListingPageSlug =
      context === catalogContexts.swap ? "catalogue" : "top-ventes-braderie";
    const productsUids = productsViewed.map((product) => product.objectID);

    productsViewedEvent("low_results", hubOrListingPageSlug, productsUids);
  }

  function sendlowResultsDisplayedEvent() {
    const defaultEventData = {
      tracking_name: "lowResultsDisplayed",
      page: router?.asPath,
      hub_slug: hub?.slug || listingPage?.hub?.slug,
      listing_slug: listingPage?.slug,
      search_with_low_results: uiState,
    };

    ahoy("$track", defaultEventData);
    analytics.track("$track", defaultEventData);
  }

  async function searchResultsCount() {
    if (["loading", "stalled"].includes(status)) return;
    if (isSearchOnRentedProducts || isSearchOnLikedProducts) return;
    if (items.length >= 40) return;
    if (!isMounted) return;

    try {
      if (!products.length || contextRef.current !== context) {
        const queryString = findQueryString();
        const res = await getProducts(context, queryString);

        setProducts(res.data);
        contextRef.current = context;
        sendProductsViewedEvent(res.data);
      }

      sendlowResultsDisplayedEvent();
    } catch (error) {
      setProducts([]);
    }
  }

  useEffect(() => {
    setIsMounted(true);
    searchResultsCount();
  }, [status, items, context]);

  if (
    noLowResultSection ||
    items.length >= 40 ||
    ["loading", "stalled"].includes(status) ||
    !isMounted ||
    !isFirstPage
  )
    return null;
  if (isSearchOnRentedProducts || isSearchOnLikedProducts) return null;

  return (
    <section>
      <LowResultsDeleteFilters
        getInitializedUiState={getInitializedUiState}
        resetPersonalisedFilters={resetPersonalisedFilters}
        setUiState={setUiState}
        products={products}
      />
      <LowResultsProductSlider
        context={context}
        uiState={uiState}
        products={products}
        hub={hub}
        listingPage={listingPage}
      />
    </section>
  );
}

export default LowResultsSection;
